import React, { Component } from 'react';
import { connect } from 'react-redux';
import { message } from 'antd';
import { Form } from '@ant-design/compatible';
import { couponActions } from '../../../../store/modules/coupon';
import withModal from '../../../../lib/hoc/withModal';
import EditCouponDiscountModal from '../../../../views/coupon/common/modal/EditCouponDiscountModal';
import { asyncSimpleModal } from '../../../../lib/common';
import { COUPON_INFO_MODEL } from '../../../../store/modules/coupon/models';
import {
  COUPON_AVAILABLE_TYPE,
  COUPON_DISCOUNT_TYPE,
  COUPON_MINIMUM_PURCHASE_MINIMUM_PRICE,
} from '../../../../constants/service/app/couponConstants';
import { DISCOUNT_TYPE } from '../../../../constants/service/app/productConstants';
import { useCouponFormStore } from '../../../../store/couponFormStore';

/**
 *  쿠폰 할인 편집 모달
 */
class EditCouponDiscountModalContainer extends Component {
  constructor(props) {
    super(props);

    const defaultSettlementShare = this.props.data.settlementShare || {
      shareType: 'rate',
      shareAmount: 0,
    };

    this.state = {
      discountType: this.props.data.discountType,
      minimumPurchasePrice: this.props.data.minimumPurchasePrice,
      discountAmount: this.props.data.discountAmount,
      maxDiscountPrice: this.props.data.maxDiscountPrice,
      settlementShare: defaultSettlementShare,
    };
  }

  /**
   *  편집 내용 적용하기
   */
  onSubmit = () => {
    const { form, setData, data, resetCouponRelatedItemList } = this.props;
    const { discountType, minimumPurchasePrice, discountAmount, maxDiscountPrice, settlementShare } = this.state;
    const { availableType } = data;

    form.validateFieldsAndScroll({ scroll: { offsetBottom: 100 } }, async (err) => {
      if (err) return;

      /**
       *  쿠폰 할인을 적용했을때 최소 결제 금액에 대한 조건식
       */
      if (discountType === DISCOUNT_TYPE.FIXED_AMOUNT_DISCOUNT) {
        if (minimumPurchasePrice - discountAmount < COUPON_MINIMUM_PURCHASE_MINIMUM_PRICE) {
          message.error('쿠폰할인액은 상품 최소 결제 금액 이상이 될 수 없습니다.');
          return;
        }
      } else if (discountType === DISCOUNT_TYPE.FIXED_RATE_DISCOUNT) {
        if (
          minimumPurchasePrice - Math.min((minimumPurchasePrice * discountAmount) / 100, maxDiscountPrice) <
          COUPON_MINIMUM_PURCHASE_MINIMUM_PRICE
        ) {
          message.error('쿠폰할인액은 상품 최소 결제 금액 이상이 될 수 없습니다.');
          return;
        }
      }

      /**
       *  쿠폰 유형이 특정 상품, 특정 브랜드 쿠폰이면 실행한다.
       */
      if (availableType !== COUPON_AVAILABLE_TYPE.TOTAL_PRODUCT_COUPON) {
        const confirmResult = await asyncSimpleModal({
          type: 'confirm',
          title: '쿠폰 할인 타입 편집하기',
          message: '상품, 브랜드 리스트 아이템이 초기화됩니다. 진행하시겠습니까?',
        });

        if (!confirmResult) return;
      }

      /**
       *  수정한 쿠폰 할인 데이터 업데이트 (Redux)
       */
      setData({
        path: ['couponCreate', 'couponInfo', 'discountType'],
        value: discountType,
      });

      setData({
        path: ['couponCreate', 'couponInfo', 'minimumPurchasePrice'],
        value: minimumPurchasePrice,
      });

      setData({
        path: ['couponCreate', 'couponInfo', 'discountAmount'],
        value: discountAmount,
      });

      /**
       *  정률 할인이면 최대 할인값도 정해줘야한다.
       */
      if (discountType === COUPON_DISCOUNT_TYPE.RATE) {
        setData({
          path: ['couponCreate', 'couponInfo', 'maxDiscountPrice'],
          value: maxDiscountPrice,
        });
      }

      /**
       *  브랜드 분담 설정 업데이트
       */
      setData({
        path: ['couponCreate', 'couponInfo', 'settlementShare'],
        value: settlementShare,
      });

      // Zustand 스토어 동기화 (coupon-v2 흐름에서만 실행)
      if (useCouponFormStore.getState()._active) {
        const { setData: zustandSetData } = useCouponFormStore.getState();
        zustandSetData({ path: ['couponCreate', 'couponInfo', 'discountType'], value: discountType });
        zustandSetData({ path: ['couponCreate', 'couponInfo', 'minimumPurchasePrice'], value: minimumPurchasePrice });
        zustandSetData({ path: ['couponCreate', 'couponInfo', 'discountAmount'], value: discountAmount });
        if (discountType === COUPON_DISCOUNT_TYPE.RATE) {
          zustandSetData({ path: ['couponCreate', 'couponInfo', 'maxDiscountPrice'], value: maxDiscountPrice });
        }
        zustandSetData({ path: ['couponCreate', 'couponInfo', 'settlementShare'], value: settlementShare });

        // setData 이후 최신 상태를 기반으로 연관 항목 초기화
        const freshState = useCouponFormStore.getState();
        useCouponFormStore.setState({
          couponProductLinkInfo: {
            ...freshState.couponProductLinkInfo,
            itemList: { ...freshState.couponProductLinkInfo.itemList, list: [] },
            selectedItemList: { list: [], idList: [] },
            selectedItemBrandCount: 0,
          },
          couponBrandLinkInfo: {
            ...freshState.couponBrandLinkInfo,
            itemList: { ...freshState.couponBrandLinkInfo.itemList, list: [] },
            selectedItemList: { list: [], idList: [] },
          },
        });
      }

      // 연관 상품 초기화 (Redux)
      resetCouponRelatedItemList();

      this.closeModal();
    });
  };

  /**
   * 쿠폰 할인 타입 변경하기
   */
  onDiscountTypeChange = (discountType) => {
    const { form } = this.props;

    /**
     *  할인율 초기화
     */
    this.setData({
      name: 'discountAmount',
      value: COUPON_INFO_MODEL.discountAmount,
    });

    form.setFieldsValue({
      discountAmount: COUPON_INFO_MODEL.discountAmount,
    });

    /**
     *  쿠폰 할인 타입 변경하기
     */
    this.setData({
      name: 'discountType',
      value: discountType,
    });

    /**
     *  브랜드 분담 타입도 쿠폰 할인 타입에 따라 자동 변경
     *  정액 할인(0) -> 정액 분담(amount)
     *  정률 할인(1) -> 정률 분담(rate)
     */
    this.setData({
      name: 'settlementShare',
      value: {
        shareType: discountType === 1 ? 'rate' : 'amount',
        shareAmount: this.state.settlementShare?.shareAmount || 0,
      },
    });
  };

  /**
   * 데이터 변경
   */
  setData = (data) => {
    this.setState({
      [data.name]: data.value,
    });
  };

  /**
   * 모달 닫기
   */
  closeModal = () => {
    this.props.toggleModal({ name: 'editCouponDiscountModal', value: false });
  };

  render() {
    const { form, isOpen, data } = this.props;

    return (
      <EditCouponDiscountModal
        {...this.state}
        form={form}
        isOpen={isOpen}
        setData={this.setData}
        onSubmit={this.onSubmit}
        closeModal={this.closeModal}
        onDiscountTypeChange={this.onDiscountTypeChange}
        isEditPage={data?.isEditPage}
      />
    );
  }
}

const mapStateToProps = (state) => {
  return {
    isOpen: state.coupon.editCouponDiscountModal.isOpen,
    data: state.coupon.editCouponDiscountModal.data,
  };
};

const mapDispatchToProps = {
  setData: couponActions.setData,
  toggleModal: couponActions.toggleModal,
  getCouponProductItemList: couponActions.getCouponProductItemList,
  getCouponBrandItemList: couponActions.getCouponBrandItemList,
  resetCouponRelatedItemList: couponActions.resetCouponRelatedItemList,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withModal()(Form.create({})(EditCouponDiscountModalContainer)));
